import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { useAuth, ADMIN_ROLES } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Modal, Field, Table } from "../../components/common";

const EMPTY_RULE = { jurisdiction: "", state: "AZ", county: "", city: "", classification: "Retail",
  rate_percent: "", effective_date: "", filing_frequency: "monthly", taxable_categories: "", exemptions: "" };

export default function AdminTaxRules() {
  const { user } = useAuth();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);

  const load = () => API.get("/admin/tax-rules").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await API.post("/admin/tax-rules", {
        ...form, taxable_categories: form.taxable_categories.split(",").map((s) => s.trim()).filter(Boolean),
        exemptions: form.exemptions.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Tax rule created");
      setOpen(false); setForm(EMPTY_RULE); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const { data: res } = await API.patch(`/admin/tax-rules/${edit.id}`, { rate_percent: edit.rate_percent, active: edit.active });
      toast.success(`Rule updated — now version ${res.new_version}`);
      setEdit(null); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  if (!data) return <Loading label="Loading tax rules" />;

  return (
    <div data-testid="admin-tax-rules">
      <PageHeader title="Tax Rules Engine" sub="Database-driven rates — no code deploys to change a rate. Every calculation stores the rule version used."
        right={isAdmin && <button className="btn-navy" onClick={() => setOpen(true)} data-testid="new-rule-btn"><Plus className="h-4 w-4" /> New Rule</button>} />

      <Table headers={["Jurisdiction", "Classification", "Rate", "Effective", "Expires", "Frequency", "Version", "Status", ""]} testid="rules-table">
        {data.rules.map((r) => (
          <tr key={r.id} className="border-b border-slate-100" data-testid={`rule-${r.jurisdiction.replace(/[^a-z]+/gi, "-")}`}>
            <td className="px-4 py-2.5"><div className="font-semibold text-navy">{r.jurisdiction}</div><div className="text-xs text-slate-400">{[r.city, r.county, r.state].filter(Boolean).join(", ")}</div></td>
            <td className="px-4 py-2.5 text-sm">{r.classification}</td>
            <td className="px-4 py-2.5 money text-lg font-bold text-copper">{r.rate_percent}%</td>
            <td className="px-4 py-2.5 text-sm">{fmtDate(r.effective_date)}</td>
            <td className="px-4 py-2.5 text-sm">{r.expiration_date ? fmtDate(r.expiration_date) : "—"}</td>
            <td className="px-4 py-2.5 text-sm capitalize">{r.filing_frequency}</td>
            <td className="px-4 py-2.5 font-mono text-xs">v{r.version}</td>
            <td className="px-4 py-2.5"><StatusBadge status={r.active ? "ACTIVE" : "CANCELLED"} /></td>
            <td className="px-4 py-2.5">{isAdmin && <button className="text-xs font-semibold text-navy hover:underline" onClick={() => setEdit({ ...r })} data-testid={`edit-rule-${r.id}`}>Edit</button>}</td>
          </tr>
        ))}
      </Table>

      <h3 className="mb-3 mt-8 font-bold text-navy">Tax Authorities</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {data.authorities.map((a) => (
          <div key={a.id} className="card-gov p-5" data-testid={`authority-${a.code}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold text-navy">{a.name}</span>
              <span className="rounded bg-navy px-2 py-0.5 font-mono text-xs text-white">{a.code}</span>
            </div>
            <div className="mt-2 text-sm text-slate-500">{a.tax_types.join(" · ")}</div>
            <div className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-500">{a.payment_instructions}</div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Tax Rule">
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-2" data-testid="rule-form">
          <Field label="Jurisdiction Name *"><input className="input-gov" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} required placeholder="Mesa, AZ (Combined)" data-testid="rule-jurisdiction" /></Field>
          <Field label="Rate % *"><input className="input-gov money" inputMode="decimal" value={form.rate_percent} onChange={(e) => setForm({ ...form, rate_percent: e.target.value })} required data-testid="rule-rate" /></Field>
          <Field label="State"><input className="input-gov" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="rule-state" /></Field>
          <Field label="City"><input className="input-gov" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="rule-city" /></Field>
          <Field label="Classification"><input className="input-gov" value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} data-testid="rule-classification" /></Field>
          <Field label="Effective Date"><input type="date" className="input-gov" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="rule-effective" /></Field>
          <Field label="Filing Frequency">
            <select className="input-gov" value={form.filing_frequency} onChange={(e) => setForm({ ...form, filing_frequency: e.target.value })} data-testid="rule-frequency">
              {["monthly", "quarterly", "annual"].map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Taxable Categories (comma-sep)"><input className="input-gov" value={form.taxable_categories} onChange={(e) => setForm({ ...form, taxable_categories: e.target.value })} data-testid="rule-categories" /></Field>
          <Field label="Exemptions (comma-sep)"><input className="input-gov sm:col-span-2" value={form.exemptions} onChange={(e) => setForm({ ...form, exemptions: e.target.value })} data-testid="rule-exemptions" /></Field>
          <button className="btn-navy sm:col-span-2" data-testid="rule-submit">Create Rule</button>
        </form>
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit Rule — ${edit?.jurisdiction || ""}`}>
        {edit && (
          <form onSubmit={saveEdit} className="space-y-4" data-testid="rule-edit-form">
            <Field label="Rate %" hint="Changing the rate creates a new rule version; historical calculations keep the old version.">
              <input className="input-gov money" inputMode="decimal" value={edit.rate_percent} onChange={(e) => setEdit({ ...edit, rate_percent: e.target.value })} data-testid="edit-rule-rate" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} data-testid="edit-rule-active" /> Active
            </label>
            <button className="btn-navy w-full" data-testid="edit-rule-submit">Save (new version)</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
